<script context="module">
    import { get } from "svelte/store";
    import {
        user,
        createUser,
        showApiErrorModal,
        isRetriedFetch,
        firstFetchErrorMsg,
    } from "../stores";
    import { HTTP_STATUS, X_Device_ID } from "../Utils/utilConstants.svelte";
    import {
        globalAbortController,
        refreshToken,
        showError,
    } from "../Utils/utilFunctions.svelte";
    import {
        serverUrl,
        checkTokenExpiration,
        signOut,
    } from "../Utils/utilFunctions.svelte";

    const noAuthUrlArr = ["FORGOTPASSWORD"];

    const doFetch = async (url, data, isManuallyHandleError = false) => {
        const userData = get(user);
        const userToken = userData == null ? "" : userData.token;

        const createUserData = get(createUser);
        const createUserToken =
            createUserData == null ? "" : createUserData.token;

        if (
            userToken ||
            createUserToken ||
            noAuthUrlArr.includes(url.split("?")[0].toUpperCase())
        ) {
            const tokenRefreshResult = noAuthUrlArr.includes(
                url.split("?")[0].toUpperCase(),
            )
                ? "valid"
                : await checkTokenExpiration();

            if (
                tokenRefreshResult != "valid" &&
                tokenRefreshResult != "refreshed"
            ) {
                signOut({ reason: "Invalid token" }, true);
                return;
            }

            try {
                var requestObject = {
                    method: "PUT",
                    headers: {
                        "Content-Type": "application/json",
                        "Access-Control-Allow-Credentials": "true",
                        "Access-Control-Allow-Origin": "*",
                        "Access-Control-Allow-Methods": "PUT",
                        Authorization: `Bearer ${
                            userToken ? userToken : createUserToken
                        }`,
                    },
                    mode: "cors",
                    cache: "no-cache",
                    signal: globalAbortController.signal,
                };

                requestObject.headers["X-Device-Id"] = X_Device_ID;

                if (data) {
                    if (typeof data === "object") data.source = `Dashboard${data.source ? ` (${data.source})` : ''}`;
                    requestObject.body = JSON.stringify(data);
                }
                const response = await fetch(
                    `${serverUrl}/${url}`,
                    requestObject,
                );

                if (response.ok) {
                    if (response.status === HTTP_STATUS.NO_CONTENT) return null;

                    let res = await response.json();

                    if (res.error && !isManuallyHandleError) {
                        console.log(res.error);
                        showError(
                            "please refresh the page and try again and contact support if the issue persists.",
                        );
                    }

                    return res;
                } else if (
                    response.status == HTTP_STATUS.SERVER_TIMEOUT ||
                    response.status == HTTP_STATUS.BAD_GATE_WAY ||
                    response.status == HTTP_STATUS.SERVICE_UNAVAILABLE ||
                    response.status == HTTP_STATUS.GATEWAY_TIMEOUT
                ) {
                    if (get(isRetriedFetch)) {
                        isRetriedFetch.set(false);
                        firstFetchErrorMsg.set("");
                        showApiErrorModal.set(true);
                        return;
                    }

                    isRetriedFetch.set(true);
                    const resText = await response.text();
                    firstFetchErrorMsg.set(resText);

                    setTimeout(async () => {
                        return await doFetch(url, data);
                    }, 1000);
                } else if (
                    response.status === HTTP_STATUS.UNAUTHORIZED ||
                    response.status === HTTP_STATUS.FORBIDDEN
                ) {
                    const tokenStatus = await checkTokenExpiration();

                    if (tokenStatus === "valid") {
                        // Refreshed from in another thread?
                        const currentUserData = get(user);
                        if (
                            currentUserData &&
                            currentUserData.token &&
                            currentUserData.token !== userToken
                        ) {
                            return await doFetch(url, data);
                        }

                        if (get(isRetriedFetch)) {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            isRetriedFetch.set(false);
                            return;
                        }

                        const localSavedUserData = JSON.parse(
                            localStorage.getItem("TciSavedUser"),
                        );

                        if (localSavedUserData) {
                            user.set({
                                ...localSavedUserData,
                            });
                        }

                        await new Promise((resolve) =>
                            setTimeout(resolve, 1000),
                        );

                        isRetriedFetch.set(true);

                        const refreshStatus = await refreshToken();

                        if (refreshStatus) {
                            return await doFetch(url, data);
                        } else {
                            signOut(
                                { url, errorStatus: response.status },
                                true,
                            );
                            return;
                        }
                    } else if (tokenStatus === "refreshed") {
                        return await doFetch(url, data);
                    } else {
                        // Already signed out from checkTokenExpiration
                        return;
                    }
                } else {
                    const errText = await response.text();
                    throw new Error(errText);
                }
            } catch (error) {
                throw error;
            }
        } else signOut({ reason: "Invalid token" }, true);
    };
    const updatePassword = async (data) => {
        const url = `ForgotPassword`;

        return await doFetch(url, data);
    };

    const updateAdmin = async (data) => {
        const url = `Admin/${data.id}`;

        return await doFetch(url, data);
    };

    const updateDoctor = async (data) => {
        const url = `Account/${data.id}`;

        return await doFetch(url, data);
    };

    const updateGroup = async (data) => {
        const url = `Group/${data.id}`;

        return await doFetch(url, data);
    };

    const updateUser = async (data) => {
        const url = `User/${data.id}`;

        return await doFetch(url, data);
    };

    const updateUserStatus = async (id, newStatus) => {
        const url = `User/UpdateStatus/${id}/${newStatus}`;

        return await doFetch(url, {});
    };

    const updateAccountInvoice = async (data) => {
        const url = `AccountInvoice/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePatient = async (data) => {
        const url = `Patient/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePatientContact = async (data) => {
        const url = `PatientContact/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePatientEncounter = async (data, includeCancelled = false, source = '') => {
        if(source){
            data = {
                ...data,
                source: `${source}`
            }
        }
        let url = `PatientEncounter/${data.id}`;

        if (includeCancelled) url += `?filter[includecancelledbillingitems]=1`;

        return await doFetch(url, data);
    };

    const updatePatientEncounterPatientClaim = async (
        encounterId,
        claimStatus,
    ) => {
        if (![0, 1].includes(claimStatus)) return;

        let url = `PatientEncounter/UpdatePatientEncounterPatientClaim/${encounterId}/${claimStatus}`;

        return await doFetch(url, {});
    };

    const unDeletePatientEncounter = async (id) => {
        const url = `PatientEncounter/UnDelete/${id}`;

        return await doFetch(url);
    };

    const insuranceDeclined = async (encounterId) => {
        const url = `PatientEncounter/InsuranceDeclined/${encounterId}`;

        return await doFetch(url);
    };

    const updateEncounterDuplicateServiceOverride = async (data) => {
        const url = `PatientEncounter/UpdateDuplicateServiceOverride/${data.id}`;

        return await doFetch(url, data);
    };

    const updateEstimate = async (data) => {
        const url = `Estimate/${data.id}`;

        return await doFetch(url, data);
    };

    const updateEstimateStatus = async (estimateId, status) => {
        const url = `Estimate/UpdateStatus/${estimateId}/${status}`;

        return await doFetch(url, {});
    };

    const sendPaperEstimate = async (id, paperProcessingFee) => {
        const url = `Estimate/SetAsPaper/${id}/${paperProcessingFee}`;

        return await doFetch(url, {});
    };

    const updateInvoice = async (data) => {
        const url = `Invoice/${data.id}`;

        return await doFetch(url, data);
    };

    const updateInvoiceStatus = async (invoiceId, status) => {
        const url = `Invoice/UpdateStatus/${invoiceId}/${status}`;

        return await doFetch(url, {});
    };

    const sendPaperInvoice = async (id, paperProcessingFee) => {
        const url = `Invoice/SetAsPaper/${id}/${paperProcessingFee}`;

        return await doFetch(url, {});
    };

    const updateAccountInsuranceFund = async (data) => {
        const url = `AccountInsuranceFundActive/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePayment = async (data) => {
        const url = `Payment/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePaymentStatus = async (id, newStatus) => {
        const url = `Payment/UpdateStatus/${id}/${newStatus}`;

        return await doFetch(url, {});
    };

    const updateEncounterStatus = async (id, newStatus) => {
        const url = `PatientEncounter/UpdateStatus/${id}/${newStatus}`;

        return await doFetch(url, {});
    };

    const cancelEncounterWithoutRefund = async (id) => {
        const url = `PatientEncounter/UpdateStatus/${id}/8?skipRefund=1`;

        return await doFetch(url, {});
    };

    const updateBillingStatus = async (id, newStatus) => {
        const url = `PatientEncounter/UpdateBillingStatus/${id}/${newStatus}`;

        return await doFetch(url, {});
    };

    const updateHandOverType = async (encounterId, newType) => {
        const url = `PatientEncounter/HandOver/${encounterId}/${newType}`;

        return await doFetch(url, {});
    };

    const updateClaimStatus = async (encounterId, newStatus) => {
        const url = `PatientEncounter/UpdateClaimStatus/${encounterId}/${newStatus}`;

        return await doFetch(url, {});
    };

    const updateClaimEraStatus = async (encounterId, newStatus) => {
        const url = `PatientEncounter/UpdateClaimEraStatus/${encounterId}/${newStatus}`;

        return await doFetch(url, {});
    };

    const updateEncounterProviderNumber = async (encounterId, providerId) => {
        const url = `PatientEncounter/UpdateProviderNumber/${encounterId}/${providerId}`;

        return await doFetch(url, {});
    };

    const updateEncounterProcedureName = async (data) => {
        const { encounterId, procedureName } = data;

        const url = `PatientEncounter/UpdateProcedureName/${encounterId}`;

        return await doFetch(url, { procedureName });
    };

    const removeEncounterClaimDelay = async (encounterId) => {
        const url = `PatientEncounter/RemoveEncounterClaimDelay/${encounterId}`;

        return await doFetch(url, {});
    };

    const updateEncounterMbsItemDescription = async (data) => {
        const {
            encounterId,
            billingDataId,
            itemNum,
            subItemNum,
            encounterItemDescription,
        } = data;

        const url = `PatientEncounter/UpdateMbsCodeDescription/${encounterId}/${billingDataId}`;

        return await doFetch(url, {
            itemNum,
            subItemNum,
            encounterItemDescription,
        });
    };

    const updateEncounterAsaItemDescription = async (data) => {
        const {
            encounterId,
            billingDataId,
            itemCode,
            subItemCode,
            encounterItemDescription,
        } = data;

        const url = `PatientEncounter/UpdateAsaCodeDescription/${encounterId}/${billingDataId}`;

        return await doFetch(url, {
            itemCode,
            subItemCode,
            encounterItemDescription,
        });
    };

    const updateDoctorStatus = async (id, newStatus) => {
        const url = `Account/UpdateStatus/${id}/${newStatus}`;

        return await doFetch(url, {});
    };

    const updateReconciliationERA = async (data) => {
        const url = `ReconciliationERA/${data.id}`;

        return await doFetch(url, data);
    };

    const updateReconciliationEFT = async (data) => {
        const url = `ReconciliationEFT/${data.id}`;

        return await doFetch(url, data);
    };

    const updateAccountProvider = async (data) => {
        const url = `AccountProvider/${data.id}`;

        return await doFetch(url, data);
    };

    const updateReceipt = async (data) => {
        const url = `Receipt/${data.id}`;

        return await doFetch(url, data);
    };

    const updateHospital = async (data) => {
        const url = `Hospital/${data.id}`;

        return await doFetch(url, data);
    };

    const updateAnnouncement = async (data) => {
        const url = `Announcement/${data.id}`;

        return await doFetch(url, data);
    };

    const editOrderProduct = async (data) => {
        const url = `OrderProducts/${data.id}`;

        return await doFetch(url, data);
    };

    const editAgreementDocument = async (data) => {
        const url = `AgreementDocuments/${data.id}`;

        return await doFetch(url, data);
    };

    const updateWorkcoverAuthority = async (data) => {
        const url = `WorkCoverAuthorities/${data.id}`;

        return await doFetch(url, data);
    };

    const updateTrafficAccidentAuthority = async (data) => {
        const url = `TrafficAccidentAuthorities/${data.id}`;

        return await doFetch(url, data);
    };

    const updateManager = async (data) => {
        const url = `Manager/${data.id}`;

        return await doFetch(url, data);
    };

    const updateAccountLeave = async (data) => {
        const url = `AccountLeave/${data.id}`;

        return await doFetch(url, data);
    };

    const updateAccountCalendarEvent = async (data) => {
        const url = `AccountCalendarEvent/${data.id}`;

        return await doFetch(url, data);
    };

    const updateGlobalEvent = async (data) => {
        const url = `GlobalCalendarEvent/${data.id}`;

        return await doFetch(url, data);
    };

    const sendPatientEncounterInsuranceDeclined = async (id) => {
        const url = `PatientEncounter/InsuranceDeclined/${id}`;

        return await doFetch(url, {});
    };

    const sendUndoEraOrEftRequest = async (type = "era", id) => {
        const url =
            type == "era"
                ? `ReconciliationERA/Undo/${id}`
                : type == "eft"
                  ? `ReconciliationEFT/Undo/${id}`
                  : "";

        if (url) return await doFetch(url, {});
    };

    const updateAccountDoctor = async (data) => {
        const url = `AccountDoctor/${data.id}`;

        return await doFetch(url, data);
    };

    const updateEmailTemplate = async (data) => {
        const url = `EmailTemplate/${data.id}`;

        return await doFetch(url, data);
    };

    const updateSmsTemplate = async (data) => {
        const url = `SmsTemplate/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePdfTemplate = async (data) => {
        const url = `PdfTemplate/${data.id}`;

        return await doFetch(url, data);
    };

    const updateSupportStaff = async (data) => {
        const url = `SupportStaff/${data.id}`;

        return await doFetch(url, data);
    };

    const updateAvatar = async (obj) => {
        const url = `image/${obj.id}`;

        const data = { ...obj };

        return await doFetch(url, data);
    };

    const updateNote = async (data) => {
        const url = `notes/${data.id}`;

        return await doFetch(url, data);
    };

    /**
      @param {Object} groupDetails
    * @param {number} groupDetails.id - group id
    * @param {string} groupDetails.domain - new tab domain
    * @returns {void}
     */
    const getSingleSignOnToken = async (groupDetails) => {
        const url = `SSO`;

        window.localStorage.setItem("ssoDetails", JSON.stringify(groupDetails));

        return await doFetch(url, { externalGroupId: groupDetails.id });
    };

    const updatePreOpInstruction = async (data) => {
        const url = `PreOpInstructions/${data.id}`;

        return await doFetch(url, data);
    };

    const updatePreOpQuestionnaire = async (data) => {
        const url = `PreOpQuestionnaire/${data.id}`;

        return await doFetch(url, data);
    };

    const copyPreOpQuestionnaire = async (fromEncounterId, toEncounterId) => {
        if (!fromEncounterId || !toEncounterId) return;

        const url = `PreOpQuestionnaire/CopyAnswers/${fromEncounterId}/${toEncounterId}`;

        return await doFetch(url, {});
    };

    const updateAccountPresets = async (data) => {
        const url = `AccountPresets/${data.id}`;

        return await doFetch(url, data);
    };

    const updateBillingRule = async (data) => {
        const url = `BillingRule/${data.id}`;

        return await doFetch(url, data);
    };

    const applyPresetToEncounter = async (
        encounterId = Number(0),
        billingDataId = Number(0),
        presetId = Number(0),
    ) => {
        if (!encounterId || !presetId) return;

        const url = `patientEncounter/applyPreset/${encounterId}/${billingDataId}/${presetId}`;

        return await doFetch(url, {});
    };

    const updateThirdPartyInsurer = async (data) => {
        const url = `AccountThirdPartyInsurers/${data.id}`;

        return await doFetch(url, data);
    };

    const updateCalendarEventCategory = async (data) => {
        const url = `CalendarEventsCategories/${data.id}`;

        return await doFetch(url, data);
    };

    const updateDefaultFilter = async (data) => {
        const url = `UsersFilters/${data.id}`;

        return await doFetch(url, data);
    };

    const updateEncounterScheduleDate = async (id, data) => {
        const url = `PatientEncounter/UpdateScheduledDate/${id}`;
        return await doFetch(url, data);
    };

    const updateSurgeryRoom = async (data) => {
        const url = `SurgeryRooms/${data.id}`;

        return await doFetch(url, data);
    };

    const updateFreeTextItem = async (data) => {
        const url = `PatientEncounterCode`;

        return await doFetch(url, data);
    };

    const updateDisableCommunication = async (encounterID, disabled) => {
        // When disabled - disable value → 1, when enabled → 0
        const url = `PatientEncounter/UpdateDisableCommunication/${encounterID}/${disabled}`;

        return await doFetch(url);
    };

    const updateEncounterPreOpQuestionnaire = async (
        encounterID,
        questionnaireID,
    ) => {
        const url = `PatientEncounter/UpdatePreOpQuestionnaire/${encounterID}/${questionnaireID}`;

        return await doFetch(url);
    };

    const updateEncounterPreOpInstruction = async (
        encounterID,
        instructionID,
    ) => {
        const url = `PatientEncounter/UpdatePreOpInstructions/${encounterID}/${instructionID}`;

        return await doFetch(url);
    };

    const updateEncounterPostOpSurvey = async (encounterID, surveyID) => {
        const url = `PatientEncounter/UpdatePostOpSurvey/${encounterID}/${surveyID}`;

        return await doFetch(url);
    };

    const updateEncounterBulkBillAndBadDebt = async (encounterID) => {
        if (!encounterID) return;

        const url = `PatientEncounter/BulkBillAndBadDebt/${encounterID}`;

        return await doFetch(url);
    };

    /**
     * @param {string} action - ResetUnderpayment / ResetOverpayment
     * @param {number} encounterId
     * @param {number} resetFor - 0 for patient, 2 for health fund
     * @param {number} amount - amount to reset
     * @returns {void}
     */
    const resetEncounterUnderOverPayment = async (
        action,
        encounterId,
        resetFor,
        amount,
    ) => {
        const url = `PatientEncounter/Reset${action}/${encounterId}/${resetFor}/${amount}`;

        return await doFetch(url);
    };

    const updateAccountInsuranceDefaultCover = async (data) => {
        const url = `AccountInsuranceDefaultCover/${data.id}`;

        return await doFetch(url, data);
    };

    const updateBupaPracticeId = async (data) => {
        const url = `BupaPracticeId/${data.id}`;

        return await doFetch(url, data);
    };

    const regenerateExternalGroupInvoice = async (data) => {
        const url = `ExternalGroupInvoice/${data.id}`;

        return await doFetch(url, data);
    };

    const savePortalCustomisation = async (data) => {
        const url = `Portal/PortalCustomisation`;

        return await doFetch(url, data);
    };

    const addListToEncounter = async (data, encounterId) => {
        const url = `OcrSchedule/${encounterId}`;

        return await doFetch(url, data);
    };

    const updateAccountThirdPartyInsurer = async (data) => {
        const url = `AccountThirdPartyInsurers/${data.id}`;

        return await doFetch(url, data);
    };

    const updateInsuranceFundIsGapProgram = async (
        fundId,
        isGapProgram = Number(0),
    ) => {
        const url = `InsuranceFund/UpdateIsGapProgram/${fundId}/${isGapProgram}`;

        return await doFetch(url, {});
    };

    const updateInsuranceFundIsOverseas = async (
        fundId,
        isOverseas = Number(0),
    ) => {
        const url = `InsuranceFund/UpdateIsOverseas/${fundId}/${isOverseas}`;

        return await doFetch(url, {});
    };

    const updateProviderIsGapProgram = async (
        uniqueId,
        isGapProgram = Number(0),
    ) => {
        const url = `AllProvider/UpdateIsGapProgram/${uniqueId}/${isGapProgram}`;

        return await doFetch(url, {});
    };

    const updateHospitalIsGapProgram = async (id, isGapProgram = Number(0)) => {
        const url = `Hospital/UpdateIsGapProgram/${id}/${isGapProgram}`;

        return await doFetch(url, {});
    };

    const updateHospitalIsNoGapProgram = async (data) => {
        const url = `HospitalsNoGapProgram/${data.id}`;

        return await doFetch(url, data);
    };

    const updateHospitalNoGapProgramRule = async (data) => {
        const url = `HospitalsNoGapProgram/SpecialRule/${data.id}`;

        return await doFetch(url, data);
    };

    export const putMethod = {
        updatePassword,
        updateAdmin,
        updateUser,
        updateAccountInvoice,
        updatePatient,
        updatePatientContact,
        updatePatientEncounter,
        updatePatientEncounterPatientClaim,
        unDeletePatientEncounter,
        insuranceDeclined,
        updateEncounterDuplicateServiceOverride,
        updateEstimate,
        updateEstimateStatus,
        sendPaperEstimate,
        updateInvoice,
        updateInvoiceStatus,
        sendPaperInvoice,
        updateAccountInsuranceFund,
        updatePayment,
        updateReconciliationERA,
        updateReconciliationEFT,
        updateDoctor,
        updateGroup,
        updatePaymentStatus,
        updateEncounterStatus,
        cancelEncounterWithoutRefund,
        updateBillingStatus,
        updateHandOverType,
        updateClaimStatus,
        updateClaimEraStatus,
        updateEncounterProviderNumber,
        updateEncounterProcedureName,
        removeEncounterClaimDelay,
        updateEncounterMbsItemDescription,
        updateEncounterAsaItemDescription,
        updateDoctorStatus,
        updateUserStatus,
        updateAccountProvider,
        updateReceipt,
        updateHospital,
        updateAnnouncement,
        updateWorkcoverAuthority,
        updateTrafficAccidentAuthority,
        updateManager,
        updateAccountLeave,
        updateAccountCalendarEvent,
        updateGlobalEvent,
        sendPatientEncounterInsuranceDeclined,
        sendUndoEraOrEftRequest,
        updateAccountDoctor,
        updateEmailTemplate,
        updateSmsTemplate,
        updatePdfTemplate,
        updateSupportStaff,
        updateAvatar,
        updateNote,
        getSingleSignOnToken,
        updatePreOpInstruction,
        updatePreOpQuestionnaire,
        copyPreOpQuestionnaire,
        updateAccountPresets,
        updateBillingRule,
        applyPresetToEncounter,
        updateThirdPartyInsurer,
        updateCalendarEventCategory,
        updateDefaultFilter,
        updateEncounterScheduleDate,
        updateSurgeryRoom,
        updateFreeTextItem,
        updateDisableCommunication,
        updateEncounterPreOpQuestionnaire,
        updateEncounterPreOpInstruction,
        updateEncounterPostOpSurvey,
        updateEncounterBulkBillAndBadDebt,
        resetEncounterUnderOverPayment,
        updateAccountInsuranceDefaultCover,
        updateBupaPracticeId,
        regenerateExternalGroupInvoice,
        savePortalCustomisation,
        addListToEncounter,
        updateAccountThirdPartyInsurer,
        updateInsuranceFundIsGapProgram,
        updateProviderIsGapProgram,
        updateHospitalIsGapProgram,
        updateHospitalIsNoGapProgram,
        updateHospitalNoGapProgramRule,
        updateInsuranceFundIsOverseas,
        editAgreementDocument,
        editOrderProduct
    };
</script>
